import { useState, useEffect } from 'react';
import AdminLayout from '../../components/AdminLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { FileText, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const getBackendUrl = () => {
  if (window.location.hostname !== 'localhost') {
    return window.location.origin;
  }
  return process.env.REACT_APP_BACKEND_URL || '';
};

const BACKEND_URL = getBackendUrl();

function AdminResources() {
  const [loading, setLoading] = useState(true);
  const [resources, setResources] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    type: 'link',
    url: '',
    category: 'General'
  });

  useEffect(() => {
    fetchResources();
  }, []);

  const fetchResources = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/resources`, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setResources(data);
      }
    } catch (error) {
      toast.error('Failed to load resources');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const response = await fetch(`${BACKEND_URL}/api/resources`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast.success('Resource added successfully');
        setDialogOpen(false);
        setFormData({
          title: '',
          description: '',
          type: 'link',
          url: '',
          category: 'General'
        });
        fetchResources();
      } else {
        throw new Error('Failed to add resource');
      }
    } catch (error) {
      toast.error('Failed to add resource');
    }
  };

  const handleDelete = async (resourceId) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/resources/${resourceId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        toast.success('Resource deleted successfully');
        fetchResources();
      } else {
        throw new Error('Failed to delete resource');
      }
    } catch (error) {
      toast.error('Failed to delete resource');
    }
  };

  if (loading) {
    return (
      <AdminLayout title="Resources">
        <div className="flex items-center justify-center h-64">
          <div className="inline-block h-12 w-12 animate-spin rounded-full border-4 border-solid border-secondary border-r-transparent"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Resources">
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-3">
            <FileText className="h-8 w-8 text-secondary" />
            <h2 className="text-2xl font-mono font-bold">Resource Management</h2>
          </div>

          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-secondary text-secondary-foreground hover:bg-secondary/90 font-mono">
                <Plus className="h-4 w-4 mr-2" />
                Add Resource
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-card border-border">
              <DialogHeader>
                <DialogTitle className="font-mono">Add Resource</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="title" className="font-mono">Title *</Label>
                  <Input
                    id="title"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    required
                    className="font-mono mt-1"
                  />
                </div>

                <div>
                  <Label htmlFor="description" className="font-mono">Description</Label>
                  <Textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={3}
                    className="font-mono mt-1"
                  />
                </div>

                <div>
                  <Label htmlFor="type" className="font-mono">Type *</Label>
                  <Select value={formData.type} onValueChange={(value) => setFormData({ ...formData, type: value })}>
                    <SelectTrigger className="font-mono mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-card border-border">
                      <SelectItem value="link">Link</SelectItem>
                      <SelectItem value="document">Document</SelectItem>
                      <SelectItem value="video">Video</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="url" className="font-mono">URL *</Label>
                  <Input
                    id="url"
                    type="url"
                    value={formData.url}
                    onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                    required
                    className="font-mono mt-1"
                    placeholder="https://..."
                  />
                </div>

                <div>
                  <Label htmlFor="category" className="font-mono">Category</Label>
                  <Input
                    id="category"
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    className="font-mono mt-1"
                    placeholder="e.g., Security, Training"
                  />
                </div>

                <Button type="submit" className="w-full font-mono">
                  Add Resource
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {resources.length === 0 ? (
          <div className="bg-card border border-border rounded-lg p-12 text-center">
            <FileText className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-xl font-mono font-bold mb-2">No Resources</h3>
            <p className="text-muted-foreground">Add your first resource to get started</p>
          </div>
        ) : (
          <div className="bg-card border border-border rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-muted/10 border-b border-border">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-mono font-bold uppercase tracking-wider">Title</th>
                    <th className="px-6 py-3 text-left text-xs font-mono font-bold uppercase tracking-wider">Type</th>
                    <th className="px-6 py-3 text-left text-xs font-mono font-bold uppercase tracking-wider">Category</th>
                    <th className="px-6 py-3 text-left text-xs font-mono font-bold uppercase tracking-wider">URL</th>
                    <th className="px-6 py-3 text-left text-xs font-mono font-bold uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {resources.map((resource) => (
                    <tr key={resource.resource_id} className="hover:bg-muted/5">
                      <td className="px-6 py-4">
                        <div>
                          <p className="font-mono font-bold">{resource.title}</p>
                          {resource.description && (
                            <p className="text-xs text-muted-foreground mt-1 line-clamp-1">{resource.description}</p>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-sm font-mono">{resource.type}</span>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-sm font-mono">{resource.category || 'General'}</span>
                      </td>
                      <td className="px-6 py-4">
                        <a
                          href={resource.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm font-mono text-accent hover:underline"
                        >
                          View
                        </a>
                      </td>
                      <td className="px-6 py-4">
                        <button
                          onClick={() => handleDelete(resource.resource_id)}
                          className="text-destructive hover:text-destructive/80"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}

export default AdminResources;